import { Box, TypographyStylesProvider } from "@mantine/core";
import React from "react";
import { IconCoinBitcoin } from "@tabler/icons-react";
import DocsButton from "../articlesHelpers/DocsButton";
import { JsonView, allExpanded, darkStyles } from "react-json-view-lite";

export const ONCHAIN_TRANSACTIONS_CONTENT = (
  <TypographyStylesProvider>
    <p>
      From the History menu, you can look at detailed data about on-chain
      transactions your node has made:
    </p>
    <DocsButton
      ariaLabel="On-chain Transactions button"
      text="On-chain Transactions"
      leftSection={<IconCoinBitcoin />}
    />
    <p>Here’s an example of an on-chain transaction record:</p>
    {/* First JSON View */}
    <Box mb="20px">
      <JsonView
        data={{
          stamp: 1729823592588,
          raw_data: {
            id: "84c97151f792dfbc8ff26e92576f12880823017b5889828ca095544f5c6e83b7",
            fee: 103294,
            inputs: [
              {
                is_local: true,
                transaction_id:
                  "e0447d7ac731dfce77371e0f0b09b190d641b48e1835ae7f54f14c22efbe3d22",
                transaction_vout: 0,
              },
            ],
            tokens: 253294,
            block_id:
              "00000000004b103381d124823d045600699a77881dfa811f7fdb509c60cb1035",
            created_at: "2024-10-25T04:13:00.000Z",
            description: "0:openchannel",
            is_outgoing: true,
            transaction:
              "02000000000101223dbeef224cf1547fae35188eb441d690b1090b0f1e3777cedf31c77a7d44e000000000000000000002f0490200000000002200200944e054c8ce9f41f4e4c2347ceb50ed22d234cd4398bf6ec91797a4120fc462d089120000000000225120048c3040bad00c265f45e0f274732b68263085b2906dec5a8bd0920d19b6eb380140b620a0f094967d3951f682de9053a9ffb25f84dd428fe7b778337dee9547d4d513ebd08604cca8438eddd77bd1a83daeedcc2c58dc6f7b1c5abbe9a5dd6e169700000000",
            is_confirmed: true,
            output_addresses: [
              "tb1qp9zwq4xge605ra8ycg68e66sa53dydxdgwvt7mkfz7t6gys0c33q37tfn2",
              "tb1pqjxrqs966qxzvh69ure8guetdqnrppdjjpk7ck5t6zfq6xdkavuqvz63es",
            ],
            confirmation_count: 2,
            confirmation_height: 3195345,
          },
        }}
        shouldExpandNode={allExpanded}
        style={darkStyles}
      />
    </Box>
    <p>
      <b>Description:</b> This transaction represents the opening of a channel
      on the Bitcoin blockchain, as indicated by the description{" "}
      <code>"0:openchannel"</code>. It is an outgoing transaction from your
      node, evidenced by <code>is_outgoing: true</code>. The funds were sent to
      two addresses, with a total amount of 253,294 satoshis. The transaction is
      confirmed with two confirmations and appears in the block hash{" "}
      <code
        style={{
          wordWrap: "break-word", // Ensures the word wraps to the next line
          wordBreak: "break-word", // Breaks the word if it overflows
          whiteSpace: "normal", // Allows text wrapping
        }}
      >
        "00000000004b103381d124823d045600699a77881dfa811f7fdb509c60cb1035"
      </code>
      .
    </p>

    {/* Third JSON View */}
    <p>Another example:</p>
    <Box mb="20px">
      <JsonView
        data={{
          stamp: 1729025910949,
          raw_data: {
            id: "ae1adcaeba672b25cb17730fe4f3c9fbd2281ba13070ea6d7d40162320968dc3",
            inputs: [
              {
                is_local: false,
                transaction_id:
                  "3c73d1ab9601cecef76c356e76ab62d096eb61c9945e1e4066c2cee1ef44188b",
                transaction_vout: 1,
              },
            ],
            tokens: 146249,
            block_id:
              "000000000eab566dd86352eca59b8c2ff166bb14447fd00b6d6221fed61f85f3",
            created_at: "2024-10-15T21:23:50.000Z",
            description: "0:closechannel:shortchanid-17592186044416000000",
            is_outgoing: false,
            transaction:
              "020000000001018b1844efe1cec266401e5e94c961eb96d062ab766e356cf7cece0196abd1733c0100000000ffffffff01493b02000000000022512023f8b071194e50a5b2b1e9afa7b77633f2116c3fdfadaccff92252dab627ecc50400473044022029f66c9cb91826e95804bc9bc834793ec3f22e28e8c4d1904f6ce692bd44a52002204a556b2bd535b967aaa26d1caacab007288bff3c98639772d4b280d18953102601483045022100e5bbc734feff5da226962cd46eed6a408795c62e651fcb22ac33b8c8dd14277802207643f939ed50db335cfdb094c48fc19a3778ce925436b1ac9c3aa1eff6b117a101475221026bdc6c464b08cb8b43e6d5f5599d2ef81e01f2e2ad07b1ff39486d49590e3b3f21027432e87db3d7a00dad579a6eb129842e4eae744d38f7d727ff72931b820c8e0f52ae00000000",
            is_confirmed: true,
            output_addresses: [
              "tb1py0utqugefeg2tv43axh60dmkx0epzmplm7k6enleyffd4d38anzsdnsssx",
            ],
            confirmation_count: 1,
            confirmation_height: 3162039,
          },
        }}
        shouldExpandNode={allExpanded}
        style={darkStyles}
      />
    </Box>
    <p>
      <b>Description:</b> This transaction represents the closing of a channel,
      as indicated by the description{" "}
      <code>"0:closechannel:shortchanid-17592186044416000000"</code>. It is an
      incoming transaction to your node (<code>is_outgoing: false</code>) with a
      total of 146,249 satoshis. The transaction is confirmed with one
      confirmation in block ID{" "}
      <code
        style={{
          wordWrap: "break-word", // Ensures the word wraps to the next line
          wordBreak: "break-word", // Breaks the word if it overflows
          whiteSpace: "normal", // Allows text wrapping
        }}
      >
        "000000000eab566dd86352eca59b8c2ff166bb14447fd00b6d6221fed61f85f3"
      </code>
      .
    </p>
  </TypographyStylesProvider>
);
